import { QueryResult } from '@apollo/client';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Form, Formik, FormikProps } from 'formik';
import { useId, useMemo, useRef, useState } from 'react';
import * as yup from 'yup';
import packageTypeIcons from '../../common/assets/packageTypeIcons';
import DropdownSetSelect from '../../common/components/form/DropdownSetSelect';
import { MultiFieldErrorMessage } from '../../common/components/form/ErrorMessage';
import FormControl from '../../common/components/form/FormControl';
import Label from '../../common/components/form/Label';
import ProgressButton from '../../common/components/form/ProgressButton';
import TextField from '../../common/components/form/TextField';
import { Col, Row } from '../../common/components/layout/Grid';
import ContentLoading from '../../common/components/loading/ContentLoading';
import { US_COUNTRY_CODE } from '../../common/constants';
import { RatesQuery, RatesQueryVariables, ShipmentBoundaries } from '../../common/gql/graphql';
import { useShipmentBoundariesQuery } from '../../common/operations/queries/shipmentBoundaries.query';
import { MEDIA_QUERY } from '../../common/styles/breakpoints';
import { GREYSCALE } from '../../common/styles/colors';
import { SPACING } from '../../common/styles/spacing';
import { TYPOGRAPHY } from '../../common/styles/typography';
import convertBbcode from '../../common/utils/convertBbcode';
import { toOunces } from '../../common/utils/formatWeight';
import { usZip } from '../../common/validation/validators';
import { useRatesLazyQuery } from '../operations/ratesConfig.query';
import DimensionCol from './DimensionCol';

export type RateResults = QueryResult<RatesQuery, RatesQueryVariables>;

function getPackageTypes() {
  const uspsMailClassKeys = () => [
    'FirstClassPackageInternationalService',
    'PriorityMailInternational',
    'PriorityMailExpressInternational',
    'PriorityExpress',
    'Priority',
    'Priority_Cubic',
    'GroundAdvantage',
    'GroundAdvantage_Cubic',
  ];
  const upsMailClassKeys = () => [
    '07',
    '08',
    '11',
    // '17', WWE disabled
    '54',
    '65',
    '14',
    '01',
    '13',
    '59',
    '02',
    '12',
    '03',
    '03_cubic',
    '92',
    '93',
  ];

  const mailClassKeys = () => [...uspsMailClassKeys(), ...upsMailClassKeys()];

  // filter out cubic services when no dimensions entered
  const pricingTypes = (dimensionX?: string, dimensionY?: string) =>
    dimensionX === '' && dimensionY === ''
      ? ['weight', 'flat_rate']
      : ['weight', 'cubic', 'flat_rate'];

  // a set of standard parameters we send with every ups request
  const upsStandardParameters = {
    dimensionX: 10,
    dimensionY: 10,
    dimensionZ: 10,
    weight: 16,
    dimensionsRequired: false,
    heightRequired: false,
    weightRequired: true,
  };

  const packageTypes = [
    {
      description: 'Any custom box or thick parcel',
      packageTypeKey: 'Parcel',
      title: 'Box or Rigid Packaging',
      dimensionsRequired: true,
      heightRequired: true,
      weightRequired: true,
      iconUrl: packageTypeIcons.Parcel,
      dimensionX: 10,
      dimensionY: 10,
      dimensionZ: 10,
      weight: undefined,
      mailClassKeys,
      packageTypeKeys: () => ['Parcel'],
      pricingTypes,
    },
    {
      title: 'Soft Pack, Padded or Flat Envelope, or Box in a Bag',
      description:
        'Measure & use the Length and Width of the Envelope before putting anything in it',
      packageTypeKey: 'SoftEnvelope',
      dimensionsRequired: true,
      heightRequired: false,
      weightRequired: true,
      iconUrl: packageTypeIcons.SoftEnvelope,
      dimensionX: 6,
      dimensionY: 3,
      dimensionZ: undefined,
      weight: 16,
      mailClassKeys,
      packageTypeKeys: () => ['SoftEnvelope'],
      pricingTypes,
    },
    {
      title: 'Tube or Irregular Packaging',
      description:
        'For unusual packages like tubes, luggage, golf or ski bags, cardboard sandwiches, wooden or metal crates, cans, tires, etc. [link=https://support.pirateship.com/en/articles/5170014-what-is-an-irregular-package intercom-link ariaLabel:"Learn More on irregular packages"]Learn More[/link]',
      packageTypeKey: 'Irregular',
      dimensionsRequired: true,
      heightRequired: true,
      weightRequired: true,
      iconUrl: packageTypeIcons.Irregular,
      dimensionX: 10,
      dimensionY: 10,
      dimensionZ: 10,
      weight: undefined,
      mailClassKeys,
      packageTypeKeys: () => ['Irregular'],
      pricingTypes,
    },
    {
      title: 'USPS Flat Rate Packaging',
      description: 'Flat Rate boxes and envelope options',
      packageTypeKey: 'UspsSmallFlatRateBox',
      dimensionsRequired: false,
      heightRequired: false,
      weightRequired: false,
      iconUrl: packageTypeIcons.FlatRatePaddedEnvelope,
      dimensionX: 10,
      dimensionY: 10,
      dimensionZ: 10,
      weight: 16,
      mailClassKeys: uspsMailClassKeys,
      packageTypeKeys: (isInternational: boolean) =>
        isInternational
          ? [
              // Do not request rates for regional rate boxes as they are not available for international shipments.
              // Do not request rates for Padded and Legal envelopes as they have the same price as normal envelopes
              // for international shipments.
              'SmallFlatRateBox',
              'MediumFlatRateBox',
              'LargeFlatRateBox',
              'FlatRateEnvelope',
              'ExpressFlatRateEnvelope',
            ]
          : [
              'SmallFlatRateBox',
              'MediumFlatRateBox',
              'LargeFlatRateBox',
              'FlatRateEnvelope',
              'FlatRatePaddedEnvelope',
              'FlatRateLegalEnvelope',
              'ExpressFlatRateEnvelope',
              'ExpressFlatRatePaddedEnvelope',
              'ExpressFlatRateLegalEnvelope',
            ],
      pricingTypes,
    },
    {
      title: 'UPS Packaging',
      description: 'UPS-branded boxes and envelopes',
      iconUrl: packageTypeIcons['2b'],
      linkToSet: 'UPS',
      packageTypeKey: 'LINK_TO_UPS',
      mailClassKeys,
      packageTypeKeys: () => [],
      pricingTypes,
    },
    {
      linkToSet: 'default',
      packageTypeKey: 'LINK_TO_DEFAULT',
      set: 'UPS',
      title: 'Back to other package types',
      mailClassKeys,
      packageTypeKeys: () => [],
      pricingTypes,
    },
    {
      title: 'UPS Express Box Small',
      description: 'Small Express Box',
      packageTypeKey: '2a',
      set: 'UPS',
      iconUrl: packageTypeIcons['2a'],
      ...upsStandardParameters,
      mailClassKeys: upsMailClassKeys,
      packageTypeKeys: () => ['2a'],
      pricingTypes,
    },
    {
      title: 'UPS Express Box Medium',
      description: 'Medium Express Box',
      packageTypeKey: '2b',
      set: 'UPS',
      iconUrl: packageTypeIcons['2b'],
      ...upsStandardParameters,
      mailClassKeys: upsMailClassKeys,
      packageTypeKeys: () => ['2b'],
      pricingTypes,
    },
    {
      title: 'UPS Express Box Large',
      description: 'Large Express Box',
      packageTypeKey: '2c',
      set: 'UPS',
      iconUrl: packageTypeIcons['2c'],
      ...upsStandardParameters,
      mailClassKeys: upsMailClassKeys,
      packageTypeKeys: () => ['2c'],
      pricingTypes,
    },
    {
      title: 'UPS Express Tube',
      description: 'Totally Tubular',
      packageTypeKey: '03',
      set: 'UPS',
      iconUrl: packageTypeIcons['03'],
      ...upsStandardParameters,
      mailClassKeys: upsMailClassKeys,
      packageTypeKeys: () => ['03'],
      pricingTypes,
    },
    {
      title: 'UPS Express Pak',
      description: 'Polymailer Express Pak',
      packageTypeKey: '04',
      set: 'UPS',
      iconUrl: packageTypeIcons['04'],
      ...upsStandardParameters,
      mailClassKeys: upsMailClassKeys,
      packageTypeKeys: () => ['04'],
      pricingTypes,
    },
    {
      title: 'UPS Express Envelope',
      description: 'Cardboard Envelope',
      packageTypeKey: '01',
      set: 'UPS',
      iconUrl: packageTypeIcons['01'],
      ...upsStandardParameters,
      mailClassKeys: upsMailClassKeys,
      packageTypeKeys: () => ['01'],
      pricingTypes,
    },
  ];

  if (Date.now() < new Date('2025-07-13').getTime()) {
    packageTypes.filter((p) => p.packageTypeKey !== 'Irregular');
  }

  return packageTypes;
}

const Styled = {
  Small: styled.small`
    text-align: right;
    display: block;
  `,
  CalcWrapper: styled.div`
    position: relative;
    max-width: 500px;
    margin: 0 auto;

    @media (max-width: ${MEDIA_QUERY.xsMax}) {
      max-width: 350px;
    }
  `,
  CalcLoading: styled.div`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 1;
  `,
  CalcContent: styled.div<{ loading: boolean }>`
    transition: opacity 0.3s ease-in-out;
    opacity: 1;

    ${({ loading }) =>
      loading &&
      css`
        opacity: 0.5;
        pointer-events: none;
      `}
  `,
  Operator: styled.span`
    display: flex;
    justify-content: center;
    margin-top: ${SPACING.lg};
    font-weight: ${TYPOGRAPHY.fontSize.sm};
    color: ${GREYSCALE.grey50};
    @media (max-width: ${MEDIA_QUERY.smMax}) {
      margin-top: ${SPACING.none};
    }
  `,

  ResidentialFlagWrapper: styled.div`
    display: flex;
    justify-content: flex-end;
  `,
  Fieldset: styled.fieldset`
    margin: 0;
    padding: 0;
    border: none;
  `,
};

export type PublicRatesCalculatorValues = {
  originZip: string;
  isResidential: boolean;
  destination: string;
  packageTypeKey: string;
  dimensionX?: string;
  dimensionY?: string;
  dimensionZ?: string;
  weightPounds: number | '';
  weightOunces: number | '';

  // virtual fields only used for validation
  combinedDimensions?: never;
  combinedWeight?: never;
};

const initialValues: PublicRatesCalculatorValues = {
  originZip: '',
  isResidential: true,
  destination: '',
  packageTypeKey: 'Parcel',
  dimensionX: '',
  dimensionY: '',
  dimensionZ: '',
  weightPounds: '',
  weightOunces: '',
};

const initValidationSchema = (
  boundaries: ShipmentBoundaries,
  currentPackageType: {
    dimensionsRequired?: boolean;
    heightRequired?: boolean;
    weightRequired?: boolean;
  },
) => {
  const { dimensionsRequired, heightRequired, weightRequired } = currentPackageType;
  return yup.object<PublicRatesCalculatorValues>({
    originZip: yup.string().usZip().required(),
    isResidential: yup.boolean(),
    destination: yup.string().required('Enter a valid zipcode or country'),
    packageTypeKey: yup.string(),
    dimensionX: yup.string().decimalNumber(),
    dimensionY: yup.string().decimalNumber(),
    dimensionZ: yup.string().decimalNumber(),
    combinedDimensions: (() => {
      if (dimensionsRequired) {
        return heightRequired
          ? yup.object().packageDimensions3dPublicRates(boundaries)
          : yup.object().packageDimensions2dPublicRates(boundaries);
      }
      return yup.object();
    })(),
    weightPounds: yup.number().min(0).default(0),
    weightOunces: yup.number().min(0).default(0),
    combinedWeight: (() =>
      weightRequired ? yup.object().packageWeight(boundaries) : yup.object())(),
  });
};

export type PublicRatesCalculatorFormProps = {
  onRateResults: (rateResults: RateResults, values: PublicRatesCalculatorValues) => void;
};

export default function PublicRatesCalculatorForm({
  onRateResults,
}: PublicRatesCalculatorFormProps) {
  const formRef = useRef<FormikProps<PublicRatesCalculatorValues>>(null);
  const [currentPackageTypeKey, setCurrentPackageTypeKey] = useState<string>('Parcel');
  const { data: boundariesData, loading: boundariesLoading } = useShipmentBoundariesQuery();

  const [fetchRates, { loading }] = useRatesLazyQuery({
    context: { errorHandled: true },
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const packageTypes = getPackageTypes();
  const packageTypeOptions = useMemo(
    () =>
      packageTypes?.map((item) => ({
        value: item.packageTypeKey,
        title: item.title,
        description: item.description ? convertBbcode(item.description) : undefined,
        iconUrl: item.iconUrl,
        set: item.set,
        linkToSet: item.linkToSet,
      })),
    [packageTypes],
  );

  const currentPackageType = useMemo(
    () => packageTypes?.find((packageType) => packageType.packageTypeKey === currentPackageTypeKey),
    [packageTypes, currentPackageTypeKey],
  );

  const validationSchema = useMemo(
    () =>
      boundariesData &&
      currentPackageType &&
      initValidationSchema(boundariesData.shipmentBoundaries, currentPackageType),
    [boundariesData, currentPackageType],
  );

  const originZipId = useId();
  const destinationId = useId();
  const packageTypeKeyId = useId();

  return (
    <Styled.CalcWrapper>
      {boundariesLoading && (
        <Styled.CalcLoading>
          <ContentLoading size={60} />
        </Styled.CalcLoading>
      )}
      <Styled.CalcContent loading={boundariesLoading}>
        <Formik
          innerRef={formRef}
          validationSchema={validationSchema}
          initialValues={initialValues}
          onSubmit={async (values) => {
            // Abort in case we are loading boundaries or rates
            if (boundariesLoading || loading) {
              return;
            }

            const {
              originZip,
              destination,
              isResidential,
              dimensionX,
              dimensionY,
              dimensionZ,
              weightPounds,
              weightOunces,
            } = values;
            const isInternational = usZip(destination) !== undefined;

            const results = await fetchRates({
              variables: {
                originZip,
                originRegionCode: '',
                isResidential,
                destinationZip: isInternational ? '' : destination,
                destinationCountryCode: isInternational ? destination : US_COUNTRY_CODE,
                mailClassKeys: currentPackageType?.mailClassKeys() || [],
                packageTypeKeys: currentPackageType?.packageTypeKeys(isInternational) || [],
                pricingTypes: currentPackageType?.pricingTypes(dimensionX, dimensionY) || [],
                dimensionX:
                  currentPackageType?.dimensionsRequired && dimensionX
                    ? +dimensionX
                    : currentPackageType?.dimensionX,
                dimensionY:
                  currentPackageType?.dimensionsRequired && dimensionY
                    ? +dimensionY
                    : currentPackageType?.dimensionY,
                dimensionZ:
                  currentPackageType?.heightRequired && dimensionZ
                    ? +dimensionZ
                    : currentPackageType?.dimensionZ,
                weight: currentPackageType?.weightRequired
                  ? toOunces({ pounds: Number(weightPounds), ounces: Number(weightOunces) })
                  : currentPackageType?.weight,
              },
            });

            onRateResults(results, values);
          }}
        >
          {({ values, errors, touched }) => {
            const packageType = packageTypes.find(
              (item) => item.packageTypeKey === values.packageTypeKey,
            );

            // Show form-level error only if all fields were touched and don't have
            // field-level errors themselves
            const showDimensionsError =
              errors.combinedDimensions !== undefined &&
              touched.dimensionX &&
              touched.dimensionY &&
              (!packageType?.heightRequired || touched.dimensionZ) &&
              !errors.dimensionX &&
              !errors.dimensionY &&
              !errors.dimensionZ;

            // Show invalid character error directly, once any dimension field is touched and contains invalid characters
            const showInvalidCharacterError =
              (errors.dimensionX !== undefined && touched.dimensionX) ||
              (errors.dimensionY !== undefined && touched.dimensionY) ||
              (values.dimensionZ !== undefined &&
                errors.dimensionZ !== undefined &&
                touched.dimensionZ);

            const showWeightError =
              errors.combinedWeight !== undefined &&
              touched.weightPounds &&
              touched.weightOunces &&
              !errors.weightPounds &&
              !errors.weightOunces;

            return (
              <Form noValidate>
                <Row spaceBelow>
                  <Col spaceBelow xs={12} md={5}>
                    <Label htmlFor={originZipId} secondary="(USA only)">
                      From
                    </Label>
                    <FormControl id={originZipId} name="originZip" as={TextField} label="Zipcode" />
                  </Col>
                  <Col spaceBelow xs={12} md={7}>
                    <Label htmlFor={destinationId} secondary="(USA zipcode or Country Name only)">
                      To
                    </Label>
                    <FormControl
                      id={destinationId}
                      name="destination"
                      as={TextField}
                      label="Zipcode or Country"
                    />
                  </Col>
                  <Col md={12} spaceBelow>
                    <Label htmlFor={packageTypeKeyId}>Type of Packaging</Label>
                    <FormControl
                      id={packageTypeKeyId}
                      name="packageTypeKey"
                      as={DropdownSetSelect}
                      options={packageTypeOptions}
                      imageSize={[70, 70]}
                      onChange={(e: string | number) => {
                        setCurrentPackageTypeKey(e as string);
                      }}
                    />
                  </Col>

                  {packageType?.dimensionsRequired && (
                    <Col xs={12} md={7}>
                      <Styled.Fieldset>
                        <legend>
                          <Label secondary="(Inches)">Dimensions</Label>
                        </legend>
                        <Row nogutter>
                          <DimensionCol
                            dimension="x"
                            spaceBelow
                            xs={packageType.heightRequired ? 4 : 6}
                          >
                            <FormControl
                              name="dimensionX"
                              as={TextField}
                              label="Length"
                              type="text"
                              inputMode="decimal"
                              // We override error here to incorporate the form-level error
                              error={
                                (touched.dimensionX && errors.dimensionX !== undefined) ||
                                showDimensionsError ||
                                showInvalidCharacterError
                              }
                              hideErrorMessage // error message is shown on combined field
                              center
                            />
                          </DimensionCol>
                          <DimensionCol
                            dimension="y"
                            spaceBelow
                            xs={packageType.heightRequired ? 4 : 6}
                            zRequired={packageType.heightRequired}
                          >
                            <FormControl
                              name="dimensionY"
                              as={TextField}
                              label="Width"
                              type="text"
                              inputMode="decimal"
                              // We override error here to incorporate the form-level error
                              error={
                                (touched.dimensionY && errors.dimensionY !== undefined) ||
                                showDimensionsError ||
                                showInvalidCharacterError
                              }
                              hideErrorMessage // error message is shown on combined field
                              center
                            />
                          </DimensionCol>
                          {packageType.heightRequired && (
                            <DimensionCol dimension="z" xs={4}>
                              <FormControl
                                name="dimensionZ"
                                as={TextField}
                                label="Height"
                                type="text"
                                inputMode="decimal"
                                error={
                                  (touched.dimensionZ && errors.dimensionZ !== undefined) ||
                                  showDimensionsError ||
                                  showInvalidCharacterError
                                }
                                hideErrorMessage // error message is shown on combined field
                                center
                              />
                            </DimensionCol>
                          )}
                          {(showInvalidCharacterError || showDimensionsError) && (
                            <Col xs={12}>
                              <MultiFieldErrorMessage className="DimensionErrors">
                                {/* Prefer invalid character msg over combinedDimension error */}
                                {errors.dimensionX ??
                                  errors.dimensionY ??
                                  errors.dimensionZ ??
                                  errors.combinedDimensions}
                              </MultiFieldErrorMessage>
                            </Col>
                          )}
                        </Row>
                      </Styled.Fieldset>
                    </Col>
                  )}
                  {packageType?.weightRequired && (
                    <Col xs={12} md={packageType.dimensionsRequired ? 5 : 12}>
                      <Styled.Fieldset>
                        <legend>
                          <Label>Weight</Label>
                        </legend>
                        <Row nogutter>
                          <Col md={5.5}>
                            <FormControl
                              aria-label="Pounds"
                              name="weightPounds"
                              as={TextField}
                              label="Pounds"
                              type="number"
                              // We override error here to incorporate the form-level error
                              error={
                                (touched.weightPounds && errors.weightPounds !== undefined) ||
                                showWeightError
                              }
                              center
                            />
                          </Col>
                          <Col md={1}>
                            <Styled.Operator>+</Styled.Operator>
                          </Col>
                          <Col spaceBelow md={5.5}>
                            <FormControl
                              aria-label="Ounces"
                              name="weightOunces"
                              as={TextField}
                              type="number"
                              label="Ounces"
                              // We override error here to incorporate the form-level error
                              error={
                                (touched.weightOunces && errors.weightOunces !== undefined) ||
                                showWeightError
                              }
                              center
                            />
                          </Col>
                          {showWeightError && (
                            <Col md={12}>
                              <MultiFieldErrorMessage className="WeightErrors">
                                {errors.combinedWeight}
                              </MultiFieldErrorMessage>
                            </Col>
                          )}
                        </Row>
                      </Styled.Fieldset>
                    </Col>
                  )}
                </Row>
                <Row>
                  <Col xs={12}>
                    <ProgressButton
                      size="xLarge"
                      variant="info"
                      title="See shipping rates"
                      type="submit"
                      fullWidth
                      progress={loading}
                    >
                      See shipping rates*
                    </ProgressButton>
                  </Col>
                </Row>
                <Styled.Small>*Rates may vary depending on actual shipping address</Styled.Small>
              </Form>
            );
          }}
        </Formik>
      </Styled.CalcContent>
    </Styled.CalcWrapper>
  );
}
